// ============================================================
// TERROR — THE FOUNTAIN OVERLAY
// V'ael's Plaza, centre of the room. Toss a Chocolate Coin.
// ============================================================
//
// Three things happen here and only three: a coin arcs into the
// water, the water gives something back, and 89% of the time the
// something is J'ynxi holding a card that isn't a card.
//
// LAWS OBSERVED
//  • Keyframes injected imperatively into document.head, id-guarded
//    AND textContent-compared. A JSX <style> block unmounts with the
//    first instance and takes the animation app-wide with it; a
//    stale sheet from a previous build survives a bare id guard and
//    silently resolves nothing. Both have cost real sessions.
//  • No browser storage. All state is React or Firebase.
//  • The fake card is deliberately NOT <CardFace>. One Face governs
//    what a CARD looks like, and this is a prop J'ynxi is holding —
//    a real face here would be a lie the player has to squint at.
//    It borrows the real holo_jynxi foil, which is the joke landing.
//  • Motion dial respected: OFF skips the arc and cuts to the result.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase";
import { GM, OBSIDIAN_SLAB, OBSIDIAN_RECESS, TEXT_SET, textGlow } from "./theme";
import { ChocolateCoin, COIN_GLYPH } from "./CoinGlyph";
import HoloFoil from "./HoloFoil";
import { getMotion, MOTION } from "./strikeLanguage";
import {
  FOUNTAIN_COST, FOUNTAIN_PRIZES, RARITY_META, ODDS, KIND_LABEL,
  ownedPrizeIds, remainingCount, fountainDayKey, tossCoin,
} from "./fountain";

// ── KEYFRAMES ─────────────────────────────────────────────────
const FOUNTAIN_CSS = `
@keyframes fnToss {
  0%   { transform: translate(-50%, 0) scale(1)    rotate(0deg);   opacity: 1; }
  45%  { transform: translate(-50%, -130px) scale(0.78) rotate(320deg); opacity: 1; }
  100% { transform: translate(-50%, 26px) scale(0.30) rotate(700deg); opacity: 0; }
}
@keyframes fnSplash {
  0%   { transform: translate(-50%, -50%) scale(0.2); opacity: 0; }
  25%  { opacity: 0.85; }
  100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
}
@keyframes fnRipple {
  0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0.55; }
  100% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
}
@keyframes fnRise {
  0%   { transform: translateY(26px) scale(0.9); opacity: 0; }
  55%  { transform: translateY(-6px) scale(1.02); opacity: 1; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
}
@keyframes fnSway {
  0%,100% { transform: rotate(-1.6deg) translateY(0); }
  50%     { transform: rotate(1.6deg) translateY(-5px); }
}
@keyframes fnGlint {
  0%   { transform: translateX(-120%) skewX(-18deg); }
  100% { transform: translateX(320%)  skewX(-18deg); }
}
@keyframes fnWater {
  0%,100% { opacity: 0.30; transform: scale(1); }
  50%     { opacity: 0.55; transform: scale(1.05); }
}
`;

function ensureFountainKeyframes() {
  if (typeof document === "undefined") return;
  const ID = "terror-fountain-keys";
  let el = document.getElementById(ID);
  if (el && el.textContent === FOUNTAIN_CSS) return;
  if (!el) { el = document.createElement("style"); el.id = ID; document.head.appendChild(el); }
  el.textContent = FOUNTAIN_CSS;
}
ensureFountainKeyframes();

// ── LIVE DATA ─────────────────────────────────────────────────
// Reads ownership from each system's REAL unlock path — there is no
// separate fountain ledger to fall out of sync with the Atelier.
export function useFountainData(uid) {
  const [coins, setCoins] = useState(0);
  const [backdrops, setBackdrops] = useState({});
  const [pulses, setPulses] = useState({});
  const [lastWinDay, setLastWinDay] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!uid) return;
    const offs = [
      onValue(ref(db, `profiles/${uid}/chocolateCoins`), s => setCoins(s.val() || 0)),
      onValue(ref(db, `profiles/${uid}/roomBackdrops/unlocked`), s => setBackdrops(s.val() || {})),
      onValue(ref(db, `profiles/${uid}/drawPulse/unlocked`), s => setPulses(s.val() || {})),
      onValue(ref(db, `profiles/${uid}/fountain/lastWinDay`), s => { setLastWinDay(s.val() || null); setReady(true); }),
    ];
    return () => offs.forEach(f => f && f());
  }, [uid]);

  const owned = ownedPrizeIds(backdrops, pulses);
  return {
    coins, owned, ready, lastWinDay,
    remaining: remainingCount(owned),
    total: FOUNTAIN_PRIZES.length,
    claimedToday: !!lastWinDay && lastWinDay === fountainDayKey(),
  };
}

// ── THE CARD SLAB ─────────────────────────────────────────────
// One shape, two uses: J'ynxi's fake and the real prize. Same slab
// deliberately — the reveal reads as "a card came out of the water"
// either way, and the ONLY thing that tells you which is the foil
// and the words. That is the whole gag, so it must be one component.
function RevealCard({ tone, kicker, title, body, footer, tier, sway }) {
  return (
    <div style={{
      position: "relative", width: 244, minHeight: 322,
      borderRadius: 14, overflow: "hidden", isolation: "isolate",
      background: OBSIDIAN_SLAB,
      border: `2px solid ${tone}`,
      boxShadow: `0 0 34px ${tone}55, 0 18px 44px rgba(0,0,0,0.75), inset 0 0 40px ${tone}18`,
      animation: sway ? "fnRise 620ms cubic-bezier(.2,.9,.3,1) both, fnSway 4.4s ease-in-out 620ms infinite" : "fnRise 620ms cubic-bezier(.2,.9,.3,1) both",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ position: "absolute", inset: 0, zIndex: 0,
        background: `radial-gradient(ellipse at 50% 22%, ${tone}2e 0%, transparent 62%)` }} />
      <HoloFoil tier={tier} radius={12} intensity={0.95} />

      <div style={{ position: "relative", zIndex: 2, padding: "20px 18px 18px",
        display: "flex", flexDirection: "column", height: "100%", flex: 1 }}>
        <div style={{ fontFamily: GM.font, fontSize: 9, letterSpacing: 4,
          color: tone, textShadow: textGlow(tone), marginBottom: 12 }}>
          {kicker}
        </div>
        <div style={{ fontFamily: GM.fontD, fontSize: 21, lineHeight: 1.18,
          color: GM.vellum, textShadow: TEXT_SET, marginBottom: 14 }}>
          {title}
        </div>
        <div style={{ fontFamily: GM.fontBody, fontSize: 15, lineHeight: 1.5,
          color: GM.vellumDim, textShadow: TEXT_SET, flex: 1 }}>
          {body}
        </div>
        {footer && (
          <div style={{ fontFamily: GM.fontUI, fontSize: 10, letterSpacing: 2,
            color: tone, opacity: 0.9, marginTop: 14, textShadow: TEXT_SET }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ── THE WATER ─────────────────────────────────────────────────
function Basin({ splashKey, still }) {
  return (
    <div style={{ position: "relative", width: 200, height: 76, margin: "0 auto" }}>
      <div style={{ position: "absolute", left: "50%", top: "50%",
        transform: "translate(-50%,-50%)", width: 176, height: 60, borderRadius: "50%",
        background: "radial-gradient(ellipse at 50% 40%, #7b52c9 0%, #3a2166 55%, #170e2c 100%)",
        boxShadow: "inset 0 6px 22px rgba(0,0,0,0.7), 0 0 30px rgba(139,92,246,0.35)",
        border: "1px solid rgba(192,132,252,0.4)" }} />
      <div style={{ position: "absolute", left: "50%", top: "50%",
        transform: "translate(-50%,-50%)", width: 150, height: 46, borderRadius: "50%",
        background: "radial-gradient(ellipse at 50% 45%, rgba(192,132,252,0.55), transparent 70%)",
        animation: still ? "none" : "fnWater 3.6s ease-in-out infinite", pointerEvents: "none" }} />
      {splashKey > 0 && (
        <React.Fragment key={splashKey}>
          <div style={{ position: "absolute", left: "50%", top: "50%", width: 90, height: 32,
            borderRadius: "50%", border: "2px solid rgba(216,180,254,0.9)",
            animation: "fnSplash 720ms ease-out forwards", pointerEvents: "none" }} />
          <div style={{ position: "absolute", left: "50%", top: "50%", width: 120, height: 42,
            borderRadius: "50%", border: "1px solid rgba(192,132,252,0.6)",
            animation: "fnRipple 1000ms 120ms ease-out forwards", pointerEvents: "none" }} />
        </React.Fragment>
      )}
    </div>
  );
}

// ── THE OVERLAY ───────────────────────────────────────────────
export default function FountainOverlay({ uid, onClose }) {
  const { coins, owned, remaining, total, claimedToday, ready } = useFountainData(uid);
  const [phase, setPhase] = useState("idle");   // idle | tossing | result
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [splash, setSplash] = useState(0);
  const busy = useRef(false);
  const timers = useRef([]);

  const still = getMotion() === MOTION.OFF;

  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  const canToss = ready && !claimedToday && remaining > 0 && coins >= FOUNTAIN_COST && phase === "idle";

  const throwCoin = useCallback(async () => {
    if (busy.current || !canToss) return;
    busy.current = true;
    setError(null); setResult(null); setPhase("tossing");

    // Fire the request immediately; the animation runs alongside it
    // rather than after, so a slow network never becomes a slow toss.
    const pending = tossCoin(uid, owned);
    const beat = still ? 0 : 760;
    if (!still) timers.current.push(setTimeout(() => setSplash(s => s + 1), beat - 120));

    const [res] = await Promise.all([
      pending,
      new Promise(r => timers.current.push(setTimeout(r, beat + (still ? 0 : 360)))),
    ]);

    if (!res?.ok) {
      setError(
        res?.reason === "insufficient-coins" ? "Not enough Chocolate Coins."
        : res?.reason === "daily-cap"        ? "The water has already answered you today."
        : "The fountain did not respond. Nothing was taken."
      );
      setPhase("idle");
    } else {
      setResult(res);
      setPhase("result");
    }
    busy.current = false;
  }, [uid, owned, canToss, still]);

  const backToIdle = () => { setResult(null); setPhase("idle"); };

  // ── result card content ──
  let card = null;
  if (phase === "result" && result) {
    if (result.hit) {
      const meta = RARITY_META[result.prize.rarity];
      card = (
        <RevealCard
          tone={meta.color}
          tier={result.prize.rarity === "mythic" ? "holo_mythic"
              : result.prize.rarity === "legendary" ? "holo_gold" : "holo_silver"}
          kicker={`${meta.label} · ${KIND_LABEL[result.prize.kind] || result.prize.kind}`}
          title={result.prize.name}
          body={
            <>
              The water keeps what it is given and gives back what it
              has kept. This one is yours now — equip it wherever it
              belongs.
              {result.substituted && (
                <div style={{ marginTop: 10, fontSize: 13, color: GM.vellumFaint }}>
                  Your {RARITY_META[result.rolledTier].label.toLowerCase()} shelf is bare,
                  so the fountain reached for the next one.
                </div>
              )}
            </>
          }
          footer="ADDED TO YOUR COLLECTION"
        />
      );
    } else if (result.exhausted) {
      card = (
        <RevealCard
          tone={GM.amethystBright} tier="holo_silver"
          kicker="THE FOUNTAIN · EMPTY"
          title="Nothing Left To Give"
          body="You have taken everything the water was holding. It will fill again — new things are always sinking."
          footer="CHECKLIST COMPLETE"
        />
      );
    } else {
      card = (
        <RevealCard
          tone="#22c55e" tier="holo_jynxi" sway
          kicker="A CARD? · FROM J'YNXI"
          title="Better Luck Next Time"
          body={<span style={{ fontStyle: "italic" }}>&ldquo;{result.taunt}&rdquo;</span>}
          footer="NOT A REAL CARD"
        />
      );
    }
  }

  // ◆ THE COIN COMES BACK. Its own die, so it can land on a hit, a
  // miss, or an exhausted pool — see fountain.js. It rides UNDER the
  // reveal card rather than replacing it, because the reveal is the
  // answer to the question the player asked and this is a bonus.
  const crestNote = result?.crests ? (
    <div style={{
      marginTop: 12, padding: "8px 14px", borderRadius: 8,
      border: "1px solid #d4a84b55", background: "rgba(212,168,75,.09)",
      color: "#e8c874", fontSize: 11, letterSpacing: 1.5, fontFamily: GM.font,
    }}>
      {"\\u25c6"} The water keeps the coin and gives back {result.crests} Divine {result.crests === 1 ? "Crest" : "Crests"}.
    </div>
  ) : null;

  const pct = total ? Math.round(((total - remaining) / total) * 100) : 0;

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 400,
        background: "radial-gradient(ellipse at 50% 45%, rgba(20,10,40,0.82), rgba(3,2,8,0.94))",
        backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: "min(94vw, 520px)", maxHeight: "92vh", overflowY: "auto",
          borderRadius: 16, padding: "26px 24px 22px",
          background: OBSIDIAN_RECESS,
          border: `1px solid ${GM.pageBorder}`,
          boxShadow: "0 26px 70px rgba(0,0,0,0.8), inset 0 0 60px rgba(139,92,246,0.07)",
          fontFamily: GM.font, textAlign: "center" }}
      >
        <div style={{ fontSize: 10, letterSpacing: 5, color: GM.amethystBright,
          textShadow: textGlow(GM.amethystBright) }}>◈ V&rsquo;AEL&rsquo;S PLAZA</div>
        <div style={{ fontFamily: GM.fontD, fontSize: 25, color: GM.vellum,
          textShadow: TEXT_SET, marginTop: 6 }}>The Fountain</div>

        {/* ── checklist ── */}
        <div style={{ marginTop: 16, marginBottom: 6 }}>
          <div style={{ height: 6, borderRadius: 3, overflow: "hidden",
            background: "rgba(0,0,0,0.55)", border: `1px solid ${GM.pageBorder}` }}>
            <div style={{ width: `${pct}%`, height: "100%",
              background: `linear-gradient(90deg, ${GM.amethyst}, ${GM.amethystBright})`,
              boxShadow: `0 0 10px ${GM.amethystBright}`, transition: "width 500ms ease" }} />
          </div>
          <div style={{ fontFamily: GM.fontUI, fontSize: 10, letterSpacing: 2,
            color: GM.vellumFaint, marginTop: 7 }}>
            {total - remaining} / {total} CLAIMED · NO DUPLICATES
          </div>
        </div>

        {/* ── the water / the card ── */}
        <div style={{ position: "relative", minHeight: 344, marginTop: 10,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          {phase === "result" ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              {card}
              {crestNote}
            </div>
          ) : (
            <div style={{ width: "100%" }}>
              <div style={{ position: "relative", height: 150 }}>
                {phase === "tossing" && !still && (
                  <div style={{ position: "absolute", left: "50%", top: 66, zIndex: 3,
                    animation: "fnToss 900ms cubic-bezier(.3,.1,.7,1) forwards" }}>
                    <ChocolateCoin size={30} />
                  </div>
                )}
                <div style={{ position: "absolute", left: 0, right: 0, bottom: 6 }}>
                  <Basin splashKey={splash} still={still} />
                </div>
              </div>
              <div style={{ fontFamily: GM.fontBody, fontSize: 16, color: GM.vellumDim,
                textShadow: TEXT_SET, padding: "0 14px", minHeight: 52 }}>
                {claimedToday
                  ? "The water has already answered you today. Come back tomorrow."
                  : remaining === 0
                    ? "You have taken everything the water was holding."
                    : phase === "tossing"
                      ? "The coin turns as it falls\u2026"
                      : "Toss a coin. Most of them are just coins."}
              </div>
              <div style={{ fontFamily: GM.fontUI, fontSize: 10, letterSpacing: 2,
                color: GM.vellumFaint, marginTop: 12, display: "flex",
                justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
                <span style={{ color: RARITY_META.fable.color }}>FABLE {Math.round(ODDS.fable * 100)}%</span>
                <span style={{ color: RARITY_META.legendary.color }}>LEGENDARY {Math.round(ODDS.legendary * 100)}%</span>
                <span style={{ color: RARITY_META.mythic.color }}>MYTHIC {Math.round(ODDS.mythic * 100)}%</span>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div style={{ fontFamily: GM.fontBody, fontSize: 14, color: "#f87171",
            textShadow: TEXT_SET, marginTop: 8 }}>{error}</div>
        )}

        {/* ── actions ── */}
        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "center" }}>
          {phase === "result" ? (
            <>
              <button onClick={backToIdle} disabled={!canToss} style={btn(canToss ? GM.amethystBright : GM.silverDim, canToss)}>
                {claimedToday ? "That's Today's" : "Again"}
              </button>
              <button onClick={onClose} style={btn(GM.inkGoldBright, true)}>Leave</button>
            </>
          ) : (
            <>
              <button onClick={throwCoin} disabled={!canToss} style={btn(canToss ? GM.amethystBright : GM.silverDim, canToss)}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                  <ChocolateCoin size={15} glow={false} />
                  Toss · {FOUNTAIN_COST}{COIN_GLYPH}
                </span>
              </button>
              <button onClick={onClose} style={btn(GM.inkGoldBright, true)}>Leave</button>
            </>
          )}
        </div>

        <div style={{ fontFamily: GM.fontUI, fontSize: 10, letterSpacing: 2,
          color: coins >= FOUNTAIN_COST ? GM.vellumFaint : "#f87171", marginTop: 12 }}>
          PURSE · {coins.toLocaleString()}{COIN_GLYPH}
        </div>
      </div>
    </div>
  );
}

const btn = (color, enabled) => ({
  position: "relative", overflow: "hidden",
  padding: "11px 20px", borderRadius: 999,
  background: enabled ? "rgba(20,12,38,0.92)" : "rgba(14,10,22,0.7)",
  border: `1px solid ${color}`,
  color, fontFamily: GM.font, fontSize: 12, letterSpacing: 2,
  textShadow: enabled ? textGlow(color) : "none",
  cursor: enabled ? "pointer" : "not-allowed",
  opacity: enabled ? 1 : 0.5,
  boxShadow: enabled ? `0 0 16px ${color}33` : "none",
});
